import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import { PencilIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline';

const VendorContacts = ({ vendorId, onContactAdded }) => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingContactId, setEditingContactId] = useState(null);
  const [contactForm, setContactForm] = useState({
    name: '',
    last_name: '',
    email: '',
    phone: '',
    mobile_phone: '',
    title: '',
    is_primary: false,
    status: 'active'
  });

  useEffect(() => {
    fetchContacts();
  }, [vendorId]);

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/vendors/${vendorId}/contacts`);
      console.log('Vendor contacts response:', response.data);
      setContacts(response.data || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching vendor contacts:', err);
      setError('Failed to load contacts. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setContactForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      
      let response;
      if (editingContactId) {
        // Update existing contact
        response = await api.put(`/vendors/${vendorId}/contacts/${editingContactId}`, contactForm);
        console.log('Vendor contact updated:', response.data);
        
        // Update the contact in the list - API returns contact directly
        setContacts(prev => prev.map(contact => 
          contact.id === editingContactId ? response.data : contact
        ));
        
        // Reset edit mode
        setShowEditForm(false);
        setEditingContactId(null);
      } else {
        // Add new contact
        response = await api.post(`/vendors/${vendorId}/contacts`, contactForm);
        console.log('Vendor contact added:', response.data);
        
        // Add the new contact to the list - API returns contact directly
        setContacts(prev => [...prev, response.data]);
        
        // Hide add form
        setShowAddForm(false);
        
        // Notify parent component if callback provided
        if (onContactAdded) {
          onContactAdded(response.data);
        }
      }
      
      // Reset form
      setContactForm({
        name: '',
        last_name: '',
        email: '',
        phone: '',
        mobile_phone: '',
        title: '',
        is_primary: false,
        status: 'active'
      });
      
    } catch (err) {
      console.error('Error saving vendor contact:', err);
      setError('Failed to save contact. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (contact) => {
    // Set form values to the contact being edited
    setContactForm({
      name: contact.name || '',
      last_name: contact.last_name || '',
      email: contact.email || '',
      phone: contact.phone || '',
      mobile_phone: contact.mobile_phone || '',
      title: contact.title || '',
      is_primary: contact.is_primary || false,
      status: contact.status || 'active'
    });
    
    // Set edit mode
    setEditingContactId(contact.id);
    setShowEditForm(true);
    setShowAddForm(false);
  };

  const handleCancelEdit = () => {
    setShowEditForm(false);
    setEditingContactId(null);
    setContactForm({
      name: '',
      last_name: '',
      email: '',
      phone: '',
      mobile_phone: '',
      title: '',
      is_primary: false,
      status: 'active'
    });
  };

  const handleDelete = async (contactId) => {
    if (!window.confirm('Are you sure you want to delete this contact?')) return;
    
    try {
      setLoading(true);
      await api.delete(`/vendors/${vendorId}/contacts/${contactId}`);
      
      // Remove the deleted contact from the list
      setContacts(prev => prev.filter(contact => contact.id !== contactId));
      
      // If we were editing this contact, cancel edit mode
      if (editingContactId === contactId) {
        handleCancelEdit();
      }
    } catch (err) {
      console.error('Error deleting vendor contact:', err);
      setError('Failed to delete contact.');
    } finally {
      setLoading(false);
    }
  };

  const handleSetPrimary = async (contactId) => {
    try {
      setLoading(true);
      await api.put(`/vendors/${vendorId}/contacts/${contactId}/set-primary`);
      
      // Update contacts list to reflect new primary contact
      setContacts(prev => prev.map(contact => ({
        ...contact,
        is_primary: contact.id === contactId
      })));
    } catch (err) {
      console.error('Error setting primary vendor contact:', err);
      setError('Failed to set primary contact.');
    } finally {
      setLoading(false);
    }
  };

  if (loading && contacts.length === 0) {
    return (
      <div className="flex justify-center items-center h-40">
        <div className="w-8 h-8 border-t-2 border-b-2 border-indigo-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div>
      {error && (
        <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-md">{error}</div>
      )}
      
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h2 className="text-xl font-semibold text-gray-900">Vendor Contacts</h2>
          <p className="mt-2 text-sm text-gray-700">
            Contacts associated with this vendor
          </p>
        </div>
        {!showAddForm && !showEditForm && (
          <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
            <button
              onClick={() => setShowAddForm(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Add Contact
            </button>
          </div>
        )}
      </div>
      
      {/* Modal Overlay for Contact Form */}
      {(showAddForm || showEditForm) && (
        <div className="fixed inset-0 overflow-y-auto z-50 flex items-center justify-center">
          {/* Background overlay */}
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" 
            onClick={showEditForm ? handleCancelEdit : () => setShowAddForm(false)}
          />
          
          {/* Modal panel */}
          <div className="bg-white relative rounded-xl shadow-xl transform transition-all sm:max-w-lg sm:w-full p-8">
            <div className="absolute top-0 right-0 pt-4 pr-4">
              <button
                type="button"
                onClick={showEditForm ? handleCancelEdit : () => setShowAddForm(false)}
                className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
              >
                <span className="sr-only">Close</span>
                <XMarkIcon className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>
            
            <div className="p-8 bg-gray-50">
              <h3 className="text-xl font-bold text-gray-800 mb-2 pb-3">
                {showEditForm ? 'Edit Contact Information' : 'Add New Contact'}
              </h3>
              <p className="text-sm text-gray-500 mb-6">Complete the form fields below. Required fields are marked with an asterisk (*).</p>
              
              <form onSubmit={handleSubmit} className="space-y-8">
                {/* Personal Details Section */}
                <div>
                  <h4 className="text-base font-medium text-gray-800 mb-6">
                    Personal Details
                  </h4>
                  <div className="grid grid-cols-1 gap-y-6 gap-x-6 sm:grid-cols-2">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                        Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        id="name"
                        required
                        value={contactForm.name}
                        onChange={handleInputChange}
                        className="bg-gray-100 text-gray-800 shadow-sm ring-2 ring-indigo-500 border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-lg"
                      />
                    </div>

                    <div>
                      <label htmlFor="last_name" className="block text-sm font-medium text-gray-700 mb-1">
                        Last Name
                      </label>
                      <input
                        type="text"
                        name="last_name"
                        id="last_name"
                        value={contactForm.last_name}
                        onChange={handleInputChange}
                        className="bg-gray-100 text-gray-800 shadow-sm ring-2 ring-indigo-500 border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-lg"
                      />
                    </div>

                    <div>
                      <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                        Title
                      </label>
                      <input
                        type="text"
                        name="title"
                        id="title"
                        value={contactForm.title}
                        onChange={handleInputChange}
                        className="bg-gray-100 text-gray-800 shadow-sm ring-2 ring-indigo-500 border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-lg"
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                        Email
                      </label>
                      <input
                        type="email"
                        name="email"
                        id="email"
                        value={contactForm.email}
                        onChange={handleInputChange}
                        className="bg-gray-100 text-gray-800 shadow-sm ring-2 ring-indigo-500 border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-lg"
                      />
                    </div>

                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                        Phone
                      </label>
                      <div className="relative">
                        <input
                          type="tel"
                          name="phone"
                          id="phone"
                          value={contactForm.phone}
                          onChange={handleInputChange}
                          placeholder="(123) 456-7890"
                          pattern="^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$"
                          title="Phone number format: (123) 456-7890"
                          className="bg-gray-100 text-gray-800 shadow-sm ring-2 ring-indigo-500 border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-lg"
                        />
                        <p className="mt-1 text-xs text-gray-400">Format: (123) 456-7890</p>
                      </div>
                    </div>

                    <div>
                      <label htmlFor="mobile_phone" className="block text-sm font-medium text-gray-700 mb-1">
                        Mobile Phone
                      </label>
                      <div className="relative">
                        <input
                          type="tel"
                          name="mobile_phone"
                          id="mobile_phone"
                          value={contactForm.mobile_phone}
                          onChange={handleInputChange}
                          placeholder="(123) 456-7890"
                          pattern="^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$"
                          title="Phone number format: (123) 456-7890"
                          className="bg-gray-100 text-gray-800 shadow-sm ring-2 ring-indigo-500 border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-lg"
                        />
                        <p className="mt-1 text-xs text-gray-400">Format: (123) 456-7890</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Contact Settings Section */}
                <div className="pt-4">
                  <h4 className="text-base font-medium text-gray-800 mb-6">
                    Contact Settings
                  </h4>
                  <div className="grid grid-cols-1 gap-y-6 gap-x-6 sm:grid-cols-2">
                    <div>
                      <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
                        Status
                      </label>
                      <select
                        name="status"
                        id="status"
                        value={contactForm.status}
                        onChange={handleInputChange}
                        className="bg-gray-100 text-gray-800 shadow-sm ring-2 ring-indigo-500 border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-lg"
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    </div>
                    
                    {/* Primary Contact Toggle */}
                    <div className="mt-6">
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          name="is_primary"
                          checked={contactForm.is_primary}
                          onChange={handleInputChange}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                        <span className="ml-3 text-sm font-medium text-gray-700">Primary Contact</span>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Divider and Buttons */}
                <div className="flex items-center justify-between space-x-3 pt-8 border-t border-gray-200 mt-6">
                  <button
                    type="button"
                    className="rounded-md bg-gray-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500"
                    onClick={showEditForm ? handleCancelEdit : () => setShowAddForm(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="rounded-md bg-indigo-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                  >
                    {showEditForm ? 'Save Changes' : 'Add Contact'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      
      {/* Contact List */}
      {contacts.length > 0 ? (
        <div className="mt-8">
          <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
            <table className="min-w-full divide-y divide-gray-300">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                    Name
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                    Title
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                    Email
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                    Phone
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                    Mobile Phone
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                    Status
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                    Primary
                  </th>
                  <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {contacts.map((contact) => (
                  <tr key={contact.id}>
                    <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                      {contact.name} {contact.last_name && contact.last_name}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      {contact.title || 'N/A'}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      {contact.email ? (
                        <a href={`mailto:${contact.email}`} className="text-indigo-600 hover:text-indigo-900">
                          {contact.email}
                        </a>
                      ) : 'N/A'}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      {contact.phone ? (
                        <a href={`tel:${contact.phone}`} className="text-indigo-600 hover:text-indigo-900">
                          {contact.phone}
                        </a>
                      ) : 'N/A'}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      {contact.mobile_phone ? (
                        <a href={`tel:${contact.mobile_phone}`} className="text-indigo-600 hover:text-indigo-900">
                          {contact.mobile_phone}
                        </a>
                      ) : 'N/A'}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        contact.status === 'active' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {contact.status}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      {contact.is_primary ? (
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                          Primary
                        </span>
                      ) : (
                        <button
                          onClick={() => handleSetPrimary(contact.id)}
                          className="text-indigo-600 hover:text-indigo-900 text-xs"
                        >
                          Set Primary
                        </button>
                      )}
                    </td>
                    <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                      <div className="flex items-center space-x-3 justify-end">
                        <button
                          onClick={() => handleEdit(contact)}
                          className="text-gray-500 hover:text-indigo-600"
                          title="Edit contact"
                        >
                          <PencilIcon className="h-5 w-5" aria-hidden="true" />
                          <span className="sr-only">Edit contact</span>
                        </button>
                        <button
                          onClick={() => handleDelete(contact.id)}
                          className="text-gray-500 hover:text-red-600"
                          title="Delete contact"
                        >
                          <TrashIcon className="h-5 w-5" aria-hidden="true" />
                          <span className="sr-only">Delete contact</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="text-center py-12 bg-white mt-4 border border-gray-200 rounded-md">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
            />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No contacts</h3>
          <p className="mt-1 text-sm text-gray-500">
            This vendor doesn't have any contacts yet.
          </p>
          <div className="mt-6">
            <button
              onClick={() => setShowAddForm(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Add Contact
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorContacts; 